import { IcInvoice } from './icons';
import Button from '../button/button';
import { Save, Trash, Tick } from '../icons';
import ItemEntryTable from './invoice-item-table';
import ExpenseEntryTable from './invoice-expense-table';

const sumField = (updates: Record<string, any>, key: string) =>
  (updates[key] || []).reduce((s: number, v: any) => s + (parseFloat(v) || 0), 0);

interface Props {
  fieldUpdates: Record<string, any>;
  resolvedLabels: Record<string, string>;
  formStatus?: string;
  onSave: () => void;
  onSubmit?: () => void;
  onDiscard?: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  customer_id: 'Customer',
  vendor_id: 'Vendor',
  date: 'Date',
  bill_date: 'Bill Date',
  invoice_date: 'Invoice Date',
  due_date: 'Due Date',
  posting_time: 'Posting Time',
  payment_term_id: 'Payment Term',
  company_id: 'Company',
  exchange_rate: 'Exchange Rate',
  account_payable_id: 'Account Payable',
  account_receivable_id: 'Account Receivable',
  currency_id: 'Currency',
  supplier_currency_id: 'Supplier Currency',
  supplier_invoice_number: 'Supplier Invoice #',
  supplier_invoice_date: 'Supplier Invoice Date',
  transaction_type: 'Transaction Type',
  narration: 'Narration',
  department: 'Department',
  department_id: 'Department',
  location: 'Location',
  incoterm_id: 'Incoterm',
  region_id: 'Region',
  series_number: 'Series Number',
  attachment_url: 'Attachment',
  additional_discount_on: 'Discount On',
  additional_discount_percentage: 'Discount Percentage',
  additional_discount_amount: 'Discount Amount',
  round_off: 'Round Off',
  round_off_option: 'Round Off Option',
  is_cash: 'Cash Transaction',
  discount_amount: 'Discount Amount',
  total_invoice_amount: 'Total Amount',
  supplier_address: 'Supplier Address',
  contact_person: 'Contact Person',
  shipping_address: 'Shipping Address',
  billing_address: 'Billing Address',
  place_of_supply: 'Place of Supply',
};

const InvoicePreview = ({ fieldUpdates, resolvedLabels, formStatus, onSave, onSubmit, onDiscard }: Props) => {
  const itemTotalKey = fieldUpdates['purchase_item_entries.total_amount']
    ? 'purchase_item_entries.total_amount'
    : fieldUpdates['sales_item_entries.total_amount']
      ? 'sales_item_entries.total_amount'
      : null;
  const itemTotal = itemTotalKey ? sumField(fieldUpdates, itemTotalKey) : 0;
  const expenseTotal = sumField(fieldUpdates, 'purchase_expense_entries.total_amount');
  const grandTotal = itemTotal + expenseTotal;
  const displayKey = (key: string) => {
    const shortKey = key.startsWith('invoice_addresses.') ? key.slice('invoice_addresses.'.length) : key;
    return FIELD_LABELS[shortKey] || FIELD_LABELS[key] || key;
  };

	const entries = Object.entries(fieldUpdates).filter(([key]) => {
    if (key.startsWith('sales_item_entries.') || key.startsWith('purchase_item_entries.') || key.startsWith('purchase_expense_entries.')) return false;
    return true;
  });

  if (Object.keys(fieldUpdates).length === 0) return null;

  return (
    <div className="chatBot--preview-card">
      <div className="chatBot--preview-head">
        <span className="chatBot--preview-head-ic"><IcInvoice size={14} /></span>
        <div className="chatBot--preview-head-ttl">
          Draft Invoice
          <span className="chatBot--preview-head-sub">AI-prepared · {entries.length} fields locked</span>
        </div>
        <span className="chatBot--preview-badge">DRAFT</span>
      </div>

      <div className="chatBot--preview-body">
        {entries.map(([key, value]) => (
          <div className="chatBot--preview-row" key={key}>
            <span className="chatBot--preview-row-key">{displayKey(key)}</span>
            <span className="chatBot--preview-row-val">{resolvedLabels[key] || String(value ?? '')}</span>
          </div>
        ))}
        <ItemEntryTable fieldUpdates={fieldUpdates} resolvedLabels={resolvedLabels} />
        <ExpenseEntryTable fieldUpdates={fieldUpdates} resolvedLabels={resolvedLabels} />
        {(itemTotal > 0 || expenseTotal > 0) && (
          <div className="chatBot--preview-tot-row total" style={{ marginTop: 8 }}>
            <span>Grand Total</span>
            <span className="chatBot--preview-tot-row-val">{grandTotal.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="chatBot--preview-foot">
        <span className="chatBot--preview-foot-grow" />
        {formStatus === 'ready_to_submit' && (
          <>
            {onDiscard && (
              <Button variant="outlined" startIcon={<Trash fontSize='small' sx={{ stroke: 'currentColor', strokeWidth: 0.5 }} />} onClick={onDiscard} sx={{ mr: 1 }}>
                Discard
              </Button>
            )}
            <Button variant="outlined" startIcon={<Save fontSize='small' sx={{ stroke: 'currentColor', strokeWidth: 0.5 }} />} onClick={onSave} sx={{ mr: 1 }}>
              Save Draft
            </Button>
            {onSubmit && (
              <Button variant="contained" startIcon={<Tick fontSize='small' sx={{ stroke: 'currentColor', strokeWidth: 0.5 }} />} onClick={onSubmit}>
                Submit
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicePreview;
